"use client";

import { useEffect, useRef } from "react";
import type { VideoSource } from "@/lib/video-utils";

type Props = {
  sources: VideoSource[];
  poster?: string | null;
  ariaLabel?: string;
  className?: string;
};

/**
 * Native-controls video that only loads and plays while it's on screen.
 *
 * - preload="none": no video bytes are fetched with the page.
 * - Within half a screen of the viewport it loads metadata so the first frame
 *   shows (stands in when there's no poster).
 * - Plays muted once 50% visible, pauses when it scrolls away.
 * - A manual pause sticks — scrolling back in won't restart it.
 * - Once unmuted it still pauses off-screen but never auto-resumes with sound.
 * - prefers-reduced-motion: no autoplay; poster/first frame + controls only.
 */
export function InViewVideo({ sources, poster, ariaLabel, className }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const userPausedRef = useRef(false);
  const isAutoPausingRef = useRef(false);
  const loadStartedRef = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // React doesn't reliably reflect `muted` into SSR markup — set the property.
    video.muted = true;

    const preloadObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        preloadObserver.disconnect();
        // Skip if play() already kicked off loading — load() would abort it.
        if (loadStartedRef.current) return;
        loadStartedRef.current = true;
        video.preload = "metadata";
        video.load();
      },
      { rootMargin: "50% 0px" }
    );
    preloadObserver.observe(video);

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return () => preloadObserver.disconnect();
    }

    const handlePause = () => {
      if (isAutoPausingRef.current) {
        isAutoPausingRef.current = false;
        return;
      }
      userPausedRef.current = true;
    };
    const handlePlay = () => {
      userPausedRef.current = false;
    };

    video.addEventListener("pause", handlePause);
    video.addEventListener("play", handlePlay);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting) {
          if (userPausedRef.current || !video.muted || !video.paused) return;
          loadStartedRef.current = true;
          video.play().catch(() => {});
        } else if (!video.paused) {
          isAutoPausingRef.current = true;
          video.pause();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(video);

    return () => {
      preloadObserver.disconnect();
      observer.disconnect();
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("play", handlePlay);
    };
  }, []);

  return (
    <video
      ref={videoRef}
      muted
      playsInline
      loop
      controls
      preload="none"
      poster={poster ?? undefined}
      aria-label={ariaLabel}
      className={className}
    >
      {sources.map((source, index) => (
        <source key={index} src={source.src} type={source.type} />
      ))}
    </video>
  );
}
